import dayjs from 'dayjs';
import type { Box, CachedBox } from 'entities/Box';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { getUserName } from 'entities/User';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import {
  Contract,
  ContractEligibleForPriceRaise,
  ContractItem,
  ContractItemPriceRaise,
  ContractItemPriceRaiseStatus,
} from '../../model/types';

export const getPriceRaiseContractItem = (contract: Contract): Nullable<ContractItem> => {
  return contract.contractItems?.find((item: ContractItem) => Boolean(item.box)) || null;
};

export const getPriceRaiseCachedBox = (contract: Contract, contractItem?: Nullable<ContractItem>): Nullable<CachedBox> => {
  return contractItem?.box || getPriceRaiseContractItem(contract)?.box || null;
};

export const getPriceRaiseOriginalBox = (contract: Contract): Nullable<Box> => {
  return getPriceRaiseCachedBox(contract)?.originalBox || null;
};

const getPriceRaiseUnitPrice = (contract: Contract, box: Nullable<Box | CachedBox>): number => {
  if (!box) {
    return 0;
  }

  const priceFactor = Number(box.priceFactor ?? 1);
  let rate = Number(box.monthRate) || 0;

  if (contract.invoiceFrequencyType === InvoiceFrequencyType.DAY) {
    rate = Number(box.dailyRate) || 0;
  }

  if (contract.invoiceFrequencyType === InvoiceFrequencyType.WEEK) {
    rate = Number(box.weekRate) || 0;
  }

  return roundNumber(rate * priceFactor);
};

export const getPriceRaiseContractUnitPrice = (contract: Contract, cachedBox?: Nullable<CachedBox>): number => {
  return getPriceRaiseUnitPrice(contract, cachedBox || getPriceRaiseCachedBox(contract));
};

export const getPriceRaiseProposedUnitPrice = (contract: Contract, cachedBox?: Nullable<CachedBox>): number => {
  const box = cachedBox || getPriceRaiseCachedBox(contract);
  const originalBoxPrice = getPriceRaiseUnitPrice(contract, box?.originalBox || null);

  if (originalBoxPrice) {
    return originalBoxPrice;
  }

  return getPriceRaiseContractUnitPrice(contract, box);
};

export const getPriceRaiseCustomerName = (contract: Contract): string => {
  return getUserName(contract.user);
};

export const getPriceRaisePercent = (currentPrice: number, newPrice: number): number => {
  if (!currentPrice) {
    return 0;
  }

  return roundNumber(((newPrice - currentPrice) / currentPrice) * 100);
};

export const isPriceRaiseAboveMaxPercent = (currentPrice: number, newPrice: number, maxPriceRaisePercent: number): boolean => {
  return getPriceRaisePercent(currentPrice, newPrice) > maxPriceRaisePercent;
};

export const getPriceRaiseInputPrice = (currentPrice: number, originalBoxPrice: number, maxPriceRaisePercent: number): number => {
  const percent = getPriceRaisePercent(currentPrice, originalBoxPrice);

  if (percent <= maxPriceRaisePercent) {
    return originalBoxPrice;
  }

  return roundNumber(currentPrice * (1 + maxPriceRaisePercent / 100));
};

export const getNewPriceInvoicePeriod = (
  priceRaiseDate?: string,
  contract?: Pick<Contract, 'invoiceLeadDays' | 'invoiceFrequencyType' | 'invoiceFrequency'> | null,
): { dateFrom: string; dateTo: string } | null => {
  if (!priceRaiseDate || contract?.invoiceLeadDays == null || !contract.invoiceFrequencyType) {
    return null;
  }

  const dateFrom = dayjs(priceRaiseDate)
    .startOf('day')
    .add(contract.invoiceLeadDays + 1, 'day');

  if (!dateFrom.isValid()) {
    return null;
  }

  const dateTo = dateFrom.add(contract.invoiceFrequency || 1, contract.invoiceFrequencyType).subtract(1, 'day');

  return {
    dateFrom: dateFrom.format('YYYY-MM-DD'),
    dateTo: dateTo.format('YYYY-MM-DD'),
  };
};

export const getReviewAgainPriceRaise = (contract: ContractEligibleForPriceRaise): ContractItemPriceRaise | undefined => {
  return contract.contractItemPriceRaises?.find((raise) => raise.status === ContractItemPriceRaiseStatus.REVIEW_AGAIN);
};

export const isPriceRaiseReviewAgain = (contract: ContractEligibleForPriceRaise): boolean => {
  return Boolean(getReviewAgainPriceRaise(contract));
};

export const getContractItemPriceRaiseDto = (
  contract: Contract,
  newPrice: number,
  oldPrice?: number,
  contractItem?: Nullable<ContractItem>,
): { oldPrice: number; newPrice: number; contractItemId?: string } => {
  const item = contractItem || getPriceRaiseContractItem(contract);
  const box = getPriceRaiseCachedBox(contract, item);

  return {
    oldPrice: oldPrice ?? getPriceRaiseContractUnitPrice(contract, box),
    newPrice,
    contractItemId: item?.contractItemId,
  };
};
